import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
	FieldExpression,
	LuceneExpression,
	NodeExpression,
	parse as luceneToParse,
	term, toString as luceneToString,
} from 'lucene';
import { debounceTime, distinctUntilChanged, from, Observable, Subscription } from 'rxjs';
import { Location, NgIf, NgFor } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { State } from '../../../order/models/localization-state';
import { ECALL_DATACENTER_TYPE } from '../../../shared/models/datacenter-aggrid-view';
import { ListFilter } from '../../../shared/models/filter';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { EcallDatacenter, FieldOfEcalls } from '../../models/ecall-off-model';
import { ECallOffService } from '../../services/ecall-off.service';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'rowCreationDate';

const ECALL_FIELDS: FieldOfEcalls[] = [
	'id',
	'customerReference',
	'ecallBatches.id',
	'ecallBatches.batchReference',
	'ecallBatches.cancellationDate',
	'ecallBatches.millCode',
	'ecallVolumes.id',
	'ecallVolumes.millCode',
	'ecallVolumes.requestedVolume',
	'ecallVolumes.supplierOrderItem',
	'ecallVolumes.supplierOrderReference',
	'rowCreationDate'
];

@Component({
	selector: 'copo-ecall-off-list',
	templateUrl: './ecall-off-list.component.html',
	styleUrl: './ecall-off-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		VirtualScrollerDirective,
		NgFor,
		RouterLink,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe
	]
})
export class EcallOffListComponent implements OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;

	readonly UNDERSCORE = UNDERSCORE;

	isLoading = false;
	filters = '';
	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription?: Subscription;
	globalSearchTextSubscription?: Subscription;
	currentFilters?: ListFilter;
	mergedFilter?: LuceneExpression;
	state: State;
	currentEcallOffSearchFilter = '';

	constructor(
		private readonly eCallOffService: ECallOffService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService,
		location: Location
	) {
		this.state = <State> location.getState();
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.eCallOffService.updateEcallOffSearchFilter(value);
			this.setFilters(value);
		});
		if (this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			this.searchTextSubscription = this.eCallOffService.currentEcallOffSearchFilter
				.subscribe(currentEcallOffSearchFilter => {
					this.currentEcallOffSearchFilter = currentEcallOffSearchFilter;
				});
			this.searchTextControl.setValue(<string | undefined> this.route.snapshot.params['query'] ?? '');
			this.globalSearchTextSubscription = this.route.params.subscribe({
				next: params => {
					this.searchTextControl.setValue(<string | undefined> params['query'] ?? '');
				},
				error: (err: unknown) => {
					console.error(err);
				}
			});
		}

		this.searchTextSubscription = this.eCallOffService.currentEcallOffSearchFilter
			.subscribe(currentEcallOffSearchFilter => {
				this.currentEcallOffSearchFilter = currentEcallOffSearchFilter;
			});
		if (this.currentEcallOffSearchFilter.length > 0) {
			this.searchTextControl.setValue(this.currentEcallOffSearchFilter);
		}
		this.currentFilters = this.getAppliedFilters();
		this.mergedFilter = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		this.setFilters(this.searchTextControl.value);
	}

	get dataId() {
		return `order-tracking-order-calloff-list-${ SORT_WAY }-${ SORT_BY }-${ this.filters }`;
	}

	createQuickSearchFilter(textSearch: string) {
		const customerReferenceField: FieldExpression = {
			field: 'customerCallOffReference',
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};

		const supplierReferenceField: FieldExpression = {
			field: 'supplierOrderNumberConcat',
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};

		const customerSupplierReferenceNode: NodeExpression = {
			left: customerReferenceField,
			operator: 'OR',
			parenthesized: true,
			right: supplierReferenceField
		};

		const batchNumberField: FieldExpression = {
			field: 'batchReferenceConcat',
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};

		const customerSupplierReferenceBatchNumberNode: NodeExpression = {
			left: customerSupplierReferenceNode,
			operator: 'OR',
			parenthesized: true,
			right: batchNumberField
		};

		return luceneToString(customerSupplierReferenceBatchNumberNode);
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
		this.globalSearchTextSubscription?.unsubscribe();
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<EcallDatacenter[]>) => void) {
		provideData(from(this.eCallOffService.getEcallFromSAP(
			ECALL_FIELDS,
			this.filters,
			index,
			limit,
			new OrderBy(SORT_BY, SORT_WAY),
			false
		).then(async result => {
			await this.eCallOffService.updateEcallDatacenterData(result.results);
			return result.results;
		})));
	}

	icon(ecall: EcallDatacenter) {
		return this.eCallOffService.getIcon(ecall);
	}

	getMills(ecall?: EcallDatacenter) {
		if (!ecall?.millDescription || ecall.millDescription !== '*') {
			return '';
		}

		const millValues = ecall.millDescriptionConcat ?? ecall.millConcat;
		if (millValues) {
			const mills = millValues.split(';').filter(m => m.trim() !== '');
			const uniqueMills = [...new Set(mills)];
			return uniqueMills.join(', ');
		}
		return '';
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};
		return luceneToString(globalNode);
	}

	setFilters(fullTextStr: string) {
		this.filters = fullTextStr !== ''
			? this.mergedFilter
				? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(fullTextStr), this.mergedFilter)
				: this.createQuickSearchFilter(fullTextStr)
			: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: ECALL_DATACENTER_TYPE
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: ECALL_DATACENTER_TYPE }, relativeTo: this.route }
		);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: this.state.filter ?? [],
			datacenterType: ECALL_DATACENTER_TYPE
		};
		const appliedFilter = this.state.filter
			? listFilter
			: this.listFilterService.getAppliedFilters(ECALL_DATACENTER_TYPE) ?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
